import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Chip,
  Stack,
  alpha,
  Button,
  Switch,
  Avatar,
  Collapse,
  useTheme,
  Typography,
} from '@mui/material';

import Iconify from 'src/components/iconify';

const PERMISSION_GROUPS = [
  {
    res: 'UI_ACCESS_GROUP',
    icon: 'solar:sidebar-minimalistic-bold-duotone',
    color: '#00A76F',
    acts: [
      'ROLES',
      'USERS',
      'TICKETS',
      'DEPARTMENTS',
      'FORMS',
      'CALENDAR',
      'STATUSES',
      'ATTRIBUTES',
      'SHAREABLE_FORMS',
      'CLIENT_CREDENTIALS',
      'MY_GROUPS',
      'SETTINGS',
    ],
    icons: {
      ROLES: '/assets/icons/navbar/ic_roles.svg',
      USERS: '/assets/icons/navbar/ic_user.svg',
      TICKETS: '/assets/icons/navbar/ic_ticket.svg',
      DEPARTMENTS: '/assets/icons/navbar/ic_department.svg',
      FORMS: '/assets/icons/navbar/ic_form.svg',
      CALENDAR: '/assets/icons/navbar/ic_calendar.svg',
      STATUSES: '/assets/icons/navbar/ic_flow.svg',
      ATTRIBUTES: '/assets/icons/navbar/ic_tag.svg',
      SHAREABLE_FORMS: '/assets/icons/navbar/ic_shareable_forms.svg',
      CLIENT_CREDENTIALS: '/assets/icons/navbar/ic_client_credentials.svg',
      MY_GROUPS: '/assets/icons/navbar/my_groups.svg',
      SETTINGS: '/assets/icons/navbar/ic_lock.svg',
    },
  },
  {
    res: 'TICKET',
    icon: 'solar:ticket-bold-duotone',
    color: '#2065D1',

    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE', 'UPLOAD', 'SCOPE_DEPARTMENT', 'RESOLVE'],
  },
  {
    res: 'TICKET_COMMENT',
    icon: 'solar:chat-dots-bold-duotone',
    color: '#FF5630',
    acts: ['READ', 'CREATE'],
  },
  {
    res: 'TICKET_ASSIGNEES',
    icon: 'solar:users-group-two-rounded-bold-duotone',
    color: '#00B8D9',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'USER',
    icon: 'solar:users-group-rounded-bold-duotone',
    color: '#8E33FF',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'ROLES',
    icon: 'solar:shield-keyhole-bold-duotone',
    color: '#7A0C2E',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'DEPARTMENT',
    icon: 'solar:structure-bold-duotone',
    color: '#FDA92D',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'TAG',
    icon: 'solar:tag-price-bold-duotone',
    color: '#00A76F',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'FORM',
    icon: 'solar:document-add-bold-duotone',
    color: '#00B8D9',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'TICKET_STATUS',
    icon: 'solar:checklist-minimalistic-bold-duotone',
    color: '#3366FF',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'SHAREABLE_FORM',
    icon: 'solar:link-bold-duotone',
    color: '#003768',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'CSV',
    icon: 'ph:file-csv-bold',
    color: '#107C41',
    acts: ['UPLOAD', 'DOWNLOAD'],
  },
  {
    res: 'FILE',
    icon: 'solar:file-send-bold-duotone',
    color: '#1890FF',
    acts: ['UPLOAD', 'READ'],
  },
  {
    res: 'CLIENT_CREDENTIALS',
    icon: 'solar:key-square-bold-duotone',
    color: '#FFAB00',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'PERMISSIONS',
    icon: 'solar:key-bold-duotone',
    color: '#41107C',
    acts: ['UPDATE'],
  },
  {
    res: 'EMAIL_SETTINGS',
    icon: 'solar:letter-bold-duotone',
    color: '#B71D18',
    acts: ['MANAGE'],
  },
  {
    res: 'DASHBOARD',
    icon: 'solar:graph-up-bold-duotone',
    color: '#FF4842',
    acts: ['ADMIN', 'DEPT_MANAGER', 'OPERATOR', 'CUSTOMER'],
  },
];

export default function UserPermissionEditor({ user, userPerms, onToggle, onCancel }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // BUFFERED STATE
  const [localPerms, setLocalPerms] = useState([...userPerms]);
  const [expanded, setExpanded] = useState({ DASHBOARD: false, UI_ACCESS_GROUP: false });
  const [isProcessing, setIsProcessing] = useState(false);

  const toggleExpand = (res) => setExpanded((p) => ({ ...p, [res]: !p[res] }));

  const formatRoleName = (name) => {
    if (!name) return '';
    return name
      .replace('ROLE_', '')
      .replace(/_/g, ' ')
      .trim()
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  /**
   * RECURSIVE SYNC ENGINE
   * Sequential execution prevents backend snapshot collisions.
   */
  const processSyncQueue = (queue) => {
    if (queue.length === 0) {
      setIsProcessing(false);
      onCancel(true);
      return;
    }
    const [current, ...remaining] = queue;
    onToggle(current);
    setTimeout(() => processSyncQueue(remaining), 250);
  };

  const handleFinish = () => {
    setIsProcessing(true);
    const toAdd = localPerms.filter((p) => !userPerms.includes(p));
    const toRemove = userPerms.filter((p) => !localPerms.includes(p));
    const allChanges = [...toRemove, ...toAdd];

    if (allChanges.length === 0) {
      setIsProcessing(false);
      onCancel();
      return;
    }
    processSyncQueue(allChanges);
  };

  /**
   * TACTICAL BUFFERED TOGGLE
   * Implements Visibility Prerequisites and Cascade Revocation for users.
   */
  const handleToggleLocal = (permKey) => {
    setLocalPerms((prev) => {
      const isActive = prev.includes(permKey);
      const [res, act] = permKey.includes(':') ? permKey.split(':') : [null, null];

      let updated = isActive ? prev.filter((p) => p !== permKey) : [...prev, permKey];

      // CASE A: DISABLING
      if (isActive) {
        // Disabling READ -> Strip all functional actions in group
        if (act === 'READ') {
          updated = updated.filter((p) => !p.startsWith(`${res}:`));
        }
      }
      // CASE B: ENABLING
      else {
        // Enabling Functional Action -> Auto-enable READ
        if (act !== 'READ' && act !== 'UI_ACCESS' && res !== 'UI_ACCESS_GROUP') {
          const groupConfig = PERMISSION_GROUPS.find((g) => g.res === res);
          if (groupConfig && groupConfig.acts.includes('READ')) {
            const readKey = `${res}:READ`;
            if (!updated.includes(readKey)) updated.push(readKey);
          }
        }

        // UI_ACCESS -> READ mapping
        if (permKey.endsWith(':UI_ACCESS')) {
          const uiResource = permKey.split(':')[0];
          const readMap = {
            ROLES: 'ROLES:READ',
            USERS: 'USER:READ',
            TICKETS: 'TICKET:READ',
            DEPARTMENTS: 'DEPARTMENT:READ',
            FORMS: 'FORM:READ',
            CALENDAR: 'CALENDAR:READ',
            STATUSES: 'TICKET_STATUS:READ',
            ATTRIBUTES: 'TAG:READ',
            SHAREABLE_FORMS: 'SHAREABLE_FORM:READ',
            CLIENT_CREDENTIALS: 'CLIENT_CREDENTIALS:READ',
            SETTINGS: 'EMAIL_SETTINGS:MANAGE',
          };
          const targetRead = readMap[uiResource];
          if (targetRead && !updated.includes(targetRead)) updated.push(targetRead);
        }

        // Singleton Rule: Dashboard
        if (permKey.startsWith('DASHBOARD:')) {
          updated = updated.filter((p) => !p.startsWith('DASHBOARD:') || p === permKey);
        }
      }

      return Array.from(new Set(updated));
    });
  };

  const resolvedDepartments = useMemo(() => {
    if (
      user.departments &&
      Array.isArray(user.departments) &&
      user.departments.length > 0
    ) {
      return user.departments.map((d) => d.name || d).join(', ');
    }
    if (user.departmentNames && Array.isArray(user.departmentNames)) {
      return user.departmentNames.join(', ');
    }
    return user.departmentName || user.department?.name || 'Central Administration';
  }, [user]);

  const ActionButtonsGroup = (
    <Stack direction="row" spacing={2} justifyContent="flex-end">
      <Button
        variant="outlined"
        onClick={onCancel} // Direct exit callback without applying changes
        disabled={isProcessing}
        sx={{
          borderColor: 'divider',
          color: 'text.primary',
          borderRadius: '12px',
          px: 4,
          py: 1.2,
          fontWeight: 800,
          textTransform: 'none',
          '&:hover': { bgcolor: 'action.hover', borderColor: 'text.primary' },
        }}
      >
        {t('buttons.cancel')}
      </Button>

      <Button
        variant="contained"
        onClick={handleFinish} // Confirms changes
        disabled={isProcessing}
        startIcon={
          <Iconify icon={isProcessing ? 'eos-icons:loading' : 'solar:check-read-bold'} />
        }
        sx={{
          bgcolor: '#00A76F',
          borderRadius: '12px',
          px: 4,
          py: 1.2,
          fontWeight: 800,
          textTransform: 'none',
          boxShadow: '0 8px 16px rgba(0, 167, 111, 0.24)',
          '&:hover': { bgcolor: '#008F5D', boxShadow: 'none' },
        }}
      >
        {isProcessing ? t('buttons.saving') : t('buttons.save')}
      </Button>
    </Stack>
  );

  return (
    <Box sx={{ p: 4, width: '100%', maxWidth: '1200px', mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>{ActionButtonsGroup}</Box>

      <Box
        sx={{
          p: 4,
          mb: 6,
          bgcolor: isDark
            ? alpha(theme.palette.background.paper, 0.8)
            : 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: '24px',
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
        }}
      >
        <Avatar
          src={user.avatarUrl}
          sx={{
            width: 100,
            height: 100,
            border: '4px solid',
            borderColor: 'background.neutral',
            boxShadow: '0px 4px 10px rgba(0,0,0,0.05)',
          }}
        />
        <Box sx={{ flexGrow: 1 }}>
          <Box
            sx={{
              display: 'inline-flex',
              px: 1.5,
              py: 0.5,
              bgcolor: '#00A76F',
              color: '#FFF',
              borderRadius: '8px',
              fontSize: '0.65rem',
              fontWeight: 900,
              letterSpacing: '0.1em',
              mb: 1.5,
              textTransform: 'uppercase',
            }}
          >
            {formatRoleName(user.role)} {formatRoleName(user.role)}{' '}
            {t('rolesPage.profile')}
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'text.primary' }}>
            {user.fullName || `${user.givenName} ${user.familyName}`}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', mt: 0.5, fontWeight: 700 }}
          >
            {resolvedDepartments}
          </Typography>
        </Box>
        <Box sx={{ pr: 4, borderRight: '1px solid', borderColor: 'divider' }}>
          <Typography
            variant="overline"
            sx={{ color: 'text.disabled', fontWeight: 900, fontSize: '0.65rem' }}
          >
            EMAIL ADDRESS
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {user.email}
          </Typography>
        </Box>
      </Box>

      <Stack spacing={2.5}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            color: 'text.primary',
            px: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Iconify icon="solar:widget-5-bold-duotone" width={24} color="#00A76F" />
          {t('permissionss.editor.moduleControl')}
        </Typography>

        {PERMISSION_GROUPS.map((group) => {
          const isExpanded = !!expanded[group.res];
          const isUIGroup = group.res === 'UI_ACCESS_GROUP';
          const activeActs = group.acts.filter((act) =>
            localPerms.includes(isUIGroup ? `${act}:UI_ACCESS` : `${group.res}:${act}`)
          );

          return (
            <Box
              key={group.res}
              sx={{
                borderRadius: '20px',
                bgcolor: 'background.paper',
                border: isExpanded
                  ? `2px solid ${group.color}`
                  : `1px solid ${theme.palette.divider}`,
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isExpanded
                  ? `0 12px 24px ${alpha(group.color, 0.15)}`
                  : 'none',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ p: 2.5, cursor: 'pointer' }}
                onClick={() => toggleExpand(group.res)}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  spacing={3}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Box
                    sx={{
                      color: activeActs.length > 0 ? group.color : '#919EAB',
                      display: 'flex',
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    <Iconify icon={group.icon} width={36} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 800, color: 'text.primary' }}
                      >
                        {t(`permissionss.modules.${group.res}.title`)}
                      </Typography>
                      {group.res === 'TICKET' && (
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="soft"
                            size="small"
                            color={
                              localPerms.includes('TICKET:SCOPE_DEPARTMENT')
                                ? 'success'
                                : 'inherit'
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleLocal('TICKET:SCOPE_DEPARTMENT');
                            }}
                            startIcon={
                              <Iconify
                                icon={
                                  localPerms.includes('TICKET:SCOPE_DEPARTMENT')
                                    ? 'solar:globus-bold'
                                    : 'solar:user-bold'
                                }
                              />
                            }
                            sx={{
                              borderRadius: '8px',
                              fontSize: '0.7rem',
                              fontWeight: 800,
                              height: '24px',
                              bgcolor: localPerms.includes('TICKET:SCOPE_DEPARTMENT')
                                ? alpha('#00A76F', 0.1)
                                : alpha('#919EAB', 0.1),
                            }}
                          >
                            {localPerms.includes('TICKET:SCOPE_DEPARTMENT')
                              ? t('permissionss.editor.showingDeptTickets')
                              : t('permissionss.editor.showDeptTickets')}
                          </Button>

                          <Button
                            variant="soft"
                            size="small"
                            color={
                              localPerms.includes('TICKET:RESOLVE')
                                ? 'success'
                                : 'inherit'
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleLocal('TICKET:RESOLVE');
                            }}
                            startIcon={
                              <Iconify
                                icon={
                                  localPerms.includes('TICKET:RESOLVE')
                                    ? 'solar:shield-check-bold'
                                    : 'solar:shield-warning-bold'
                                }
                              />
                            }
                            sx={{
                              borderRadius: '8px',
                              fontSize: '0.7rem',
                              fontWeight: 800,
                              height: '24px',
                              bgcolor: localPerms.includes('TICKET:RESOLVE')
                                ? alpha('#00A76F', 0.1)
                                : alpha('#919EAB', 0.1),
                            }}
                          >
                            {localPerms.includes('TICKET:RESOLVE')
                              ? t('permissionss.editor.resolvesTickets')
                              : t('permissionss.editor.cannotResolve')}
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1 }}>
                      {activeActs.length > 0 ? (
                        activeActs.map((act) => (
                          <Chip
                            key={act}
                            label={t(`permissionss.actions.${act}.short`)}
                            size="small"
                            sx={{
                              fontSize: '0.6rem',
                              fontWeight: 900,
                              height: '20px',
                              bgcolor: alpha(group.color, 0.08),
                              color: group.color,
                              border: `1px solid ${alpha(group.color, 0.1)}`,
                              textTransform: 'uppercase',
                              borderRadius: '6px',
                            }}
                          />
                        ))
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                        >
                          {t('permissionss.editor.noAccess')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Stack>
                <Iconify
                  icon={
                    isExpanded
                      ? 'solar:alt-arrow-up-bold-duotone'
                      : 'solar:alt-arrow-down-bold-duotone'
                  }
                  color="text.disabled"
                  width={24}
                  sx={{ flexShrink: 0, ml: 2 }}
                />
              </Stack>

              <Collapse in={isExpanded}>
                <Box
                  sx={{
                    px: 4,
                    pb: 4,
                    bgcolor: 'background.neutral',
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Stack spacing={1} sx={{ mt: 3 }}>
                    {group.acts.map((action) => {
                      const k = isUIGroup
                        ? `${action}:UI_ACCESS`
                        : `${group.res}:${action}`;
                      const isActive = localPerms.includes(k);
                      const rowIcon = isUIGroup ? group.icons[action] : null;

                      return (
                        <Stack
                          key={k}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            py: 2,
                            px: 2,
                            borderRadius: '12px',
                            transition: 'background 0.2s',
                            '&:hover': { bgcolor: alpha(group.color, 0.04) },
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={2}>
                            {rowIcon && (
                              <Box
                                component="img"
                                src={rowIcon}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  opacity: isActive ? 1 : 0.3,
                                }}
                              />
                            )}
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 800,
                                  fontSize: '0.95rem',
                                  color: isActive ? 'text.primary' : 'text.disabled',
                                }}
                              >
                                {t(
                                  `permissionss.modules.${
                                    isUIGroup ? action : group.res
                                  }.actions.${isUIGroup ? 'UI_ACCESS' : action}.title`
                                )}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'block',
                                  mt: 0.3,
                                  textTransform: 'uppercase',
                                  fontSize: '0.65rem',
                                  letterSpacing: '0.05em',
                                  fontWeight: 700,
                                }}
                              >
                                {t(
                                  `permissionss.modules.${
                                    isUIGroup ? action : group.res
                                  }.actions.${isUIGroup ? 'UI_ACCESS' : action}.desc`
                                )}
                              </Typography>
                            </Box>
                          </Stack>
                          <Switch
                            checked={isActive}
                            disabled={isProcessing}
                            onChange={() => handleToggleLocal(k)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: group.color,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                bgcolor: group.color,
                              },
                            }}
                          />
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Stack>
      <Box sx={{ mt: 6 }}>{ActionButtonsGroup}</Box>
    </Box>
  );
}

UserPermissionEditor.propTypes = {
  user: PropTypes.object.isRequired,
  userPerms: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
